import { autotile } from "game-common/room/autotiler"
import { BaseMapBuilder, DebugMeta, FeatureSpec, MapEntry, TileSpec } from "game-common/room/base_map_builder"
import { ItemTypes } from "game-common/item/item_type"
import { Bounds, EntityId, TileMapBase } from "../../../models"
import { blankMap, fineToRough, fineToRoughCoordinates } from "../../../util"
import { BaseFeatureBuilder } from "../../feature/base_feature"
import { FragmentPoint } from "../../feature_catalog"
import { WallAutotileDefinition, applyWallCell, autotileTables, wallMaterialsByItem } from "../../autotile_library"
import { bindContent } from "../../../content/catalog"

export type GridCell = {
    itemType: ItemTypes
    fragmentPointer?: FragmentPointer
    children?: any
    entityType?: string
    entityId?: EntityId
    context?: any
}

export interface FragmentPointer {
    catalog: string
    tag: string
}

export interface HouseBuilderProps {
    blockingTileSpec: TileSpec
    house: TileMapBase<GridCell>
    roof: TileMapBase<GridCell>
    entity: TileMapBase<GridCell>
    floor: TileMapBase<GridCell>
    floor2: TileMapBase<GridCell>
    decor: TileMapBase<GridCell>
}

export interface HouseBuilderResults {
    coordinateCache: Map<string, MapEntry[]>
    explicitlyRemoved: MapEntry[]
}

export type EntryAdder = (
    x: number,
    y: number,
    tilePos: number,
    layerId?: string,
    tilesetId?: string,
    spriteCap?: {},
    context?: any,
) => MapEntry

export interface BuilderUtils {
    addFor: (x: number, y: number, mapEntry: MapEntry) => void
    add: (mapEntry: MapEntry) => void
    entry: (
        x: number,
        y: number,
        tilePos: number,
        layerId?: string,
        tilesetId?: string,
        spriteCap?: {},
        context?: any,
    ) => MapEntry
    exists: (x: number, y: number, layerId?: string) => boolean
}

export type Autotiler = (x: number, y: number, houseAuto: TileMapBase<number>, builderUtils: BuilderUtils) => void

/**
 * A wall material's autotiler: its definition in content/game/maps/autotiles.json (kind "wall"), resolved
 * for the cell and written through the builder's own entry()/addFor(). Sprite Lab runs the same
 * `applyWallCell`.
 */
export const wallAutotiler =
    (definition: WallAutotileDefinition): Autotiler =>
    (x, y, houseAuto, { entry, addFor, exists }) => {
        applyWallCell(definition, houseAuto, x, y, { entry, addFor, exists })
    }

export class HouseBuilder extends BaseFeatureBuilder {
    featureSpec: FeatureSpec
    roomId: string
    featureType: string
    originBiomeName: string
    finalFeatureRegionBounds: Bounds
    boundsToClear: Bounds[] = []
    featureContext: any
    clear: boolean = true
    config: HouseBuilderProps
    results: HouseBuilderResults

    /**
     * Which wall material (a kind "wall" id in content/game/maps/autotiles.json) each item type builds:
     * every wall definition's `itemType`, so a material saved from Sprite Lab is buildable as content.
     */
    static wallMaterials: Map<ItemTypes, string> = new Map()
    static wallDefinitions: Map<ItemTypes, WallAutotileDefinition> = new Map()
    static autotilers: Map<ItemTypes, Autotiler> = new Map()

    constructor(baseMapBuilder: BaseMapBuilder, config: HouseBuilderProps) {
        super(baseMapBuilder, "house")
        this.config = config
        this.results = {
            coordinateCache: new Map(),
            explicitlyRemoved: [],
        }
    }

    build = (roomId: string) => {
        this.roomId = roomId
        let entries: MapEntry[] = []
        const debug: DebugMeta[] = []

        const { blockingTileSpec, roof, house, entity, floor, floor2, decor } = this.config
        const width = house[0].length
        const height = house.length

        const cache: Map<string, MapEntry> = new Map()

        // What goes under a house cell: its wall material's `cell` decorations, or, for a cell no wall
        // definition covers, the caller's blockingTileSpec on both layers. One TileSpec per kind of
        // cell per build, as the shared blockingTileSpec was, so no entry aliases the loaded data.
        const cellDecorations = new Map<string, { blocking?: TileSpec; occlusion?: TileSpec }>()
        const decorationsFor = (itemType: ItemTypes) => {
            let decorations = cellDecorations.get(itemType)
            if (!decorations) {
                const cell = HouseBuilder.wallDefinitions.get(itemType)?.cell
                const spec = (ref?: { tilePos: number; tilesetId: string }): TileSpec | undefined =>
                    ref ? { tilePos: ref.tilePos, tilesetId: ref.tilesetId } : undefined
                decorations = cell
                    ? { blocking: spec(cell.blocking), occlusion: spec(cell.occlusion) }
                    : { blocking: blockingTileSpec, occlusion: blockingTileSpec }
                cellDecorations.set(itemType, decorations)
            }
            return decorations
        }

        const entry = (
            x: number,
            y: number,
            tilePos: number,
            layerId: string = "sprite",
            tilesetId: string = "fence_medieval",
            spriteCap?: {},
            context?: any,
        ): MapEntry => {
            const e = cache.get(`${x}-${y}-${layerId}`)
            if (e) {
                replace(x, y, tilePos, layerId)
                return find(x, y, layerId)
            }

            let mapSpriteCap
            try {
                mapSpriteCap =
                    spriteCap !== undefined
                        ? spriteCap
                        : !layerId.includes("sky") &&
                          !layerId.includes("map") &&
                          !house[y + 1]?.[x] &&
                          !!house[y + 2]?.[x]
                        ? {}
                        : undefined
            } catch (e) {
                console.log("!")
            }
            const item: MapEntry = {
                mapId: roomId,
                tileSpec: {
                    tilePos,
                    tilesetId,
                },
                layerId,
                x,
                y,
                mapSpriteCap,
                context,
            }
            cache.set(`${x}-${y}-${layerId}`, item)

            return item
        }

        const replace = (x: number, y: number, tilePos: number, layerId: string = "sprite") => {
            const e = cache.get(`${x}-${y}-${layerId}`)
            if (e) {
                e.tileSpec.tilePos = tilePos
            }
        }

        const find = (x: number, y: number, layerId: string = "sprite") => {
            const e = cache.get(`${x}-${y}-${layerId}`)
            return e
        }

        const tileAt = (x: number, y: number, layerId: string = "sprite") => {
            return find(x, y, layerId)?.tileSpec?.tilePos
        }

        const exists = (x: number, y: number, layerId: string = "sprite") => {
            return !!find(x, y, layerId)
        }

        const addFor = (x: number, y: number, mapEntry: MapEntry) => {
            if (!mapEntry) {
                return
            }
            mapEntry.context = {
                ...(mapEntry.context || {}),
                parent: {
                    x,
                    y,
                },
            }
            const key = `${x}-${y}-${mapEntry.layerId}`
            let sub = this.results.coordinateCache.get(key)
            if (!sub) {
                sub = []
                this.results.coordinateCache.set(key, sub)
            }
            sub.push(mapEntry)
            entries.push(mapEntry)

            this.results.explicitlyRemoved = this.results.explicitlyRemoved.filter(
                n => !(n.x === mapEntry.x && n.y === mapEntry.y && n.layerId === mapEntry.layerId),
            )
        }

        const add = (mapEntry: MapEntry) => {
            const { x, y } = mapEntry
            addFor(x, y, mapEntry)
        }

        const remove = (x: number, y: number, layerId: string) => {
            const key = `${x}-${y}-${layerId}`
            let sub = this.results.coordinateCache.get(key)
            if (sub) {
                sub = sub.filter(n => !(n.layerId === layerId && n.x === x && n.y === y))
                if (sub.length > 0) {
                    this.results.coordinateCache.set(key, sub)
                } else {
                    this.results.coordinateCache.delete(key)
                }
            }
            entries = entries.filter(n => !(n.layerId === layerId && n.x === x && n.y === y))

            this.results.explicitlyRemoved.push({
                x,
                y,
                layerId,
                mapId: this.roomId,
                tileSpec: null,
            })
        }

        const handleFragmentPointer = (
            x: number,
            y: number,
            fragmentPointer: FragmentPointer,
            entityId?: EntityId,
            context?: any,
        ) => {
            const fragmentMeta = this.tiledMapBuilder.featureCatalogs.featureByTag(
                fragmentPointer.catalog,
                fragmentPointer.tag,
            )

            const caps = fragmentMeta.mapSpriteCaps
                .map(n => ({
                    x: fineToRough(n.location.x),
                    y: fineToRough(n.location.y),
                    context: n.context,
                }))
                .reduce((acc, next) => ({ ...acc, [next.x + "-" + next.y]: next }), {})

            const anchor: FragmentPoint = fragmentMeta.points.find(n => n.context?.anchor)
            const anchorPos = anchor ? fineToRoughCoordinates(anchor.location) : undefined
            const clearPointsForLayers: string[] = fragmentMeta.context?.clearPointsForLayers || []
            for (let fy = 0; fy < fragmentMeta.dimensions.h; fy++) {
                for (let fx = 0; fx < fragmentMeta.dimensions.w; fx++) {
                    Object.keys(fragmentMeta.fragmentMaps).forEach(layerId => {
                        const spec = fragmentMeta.fragmentMaps[layerId]?.[fx]?.[fy]
                        if (spec) {
                            const curX = anchorPos ? fx - anchorPos.x : fx
                            const curY = anchorPos ? fy - anchorPos.y : fy
                            const isAnchorPos = fx === anchorPos?.x && fy === anchorPos?.y
                            const entryObject = entry(
                                curX + x,
                                curY + y,
                                spec.tilePos,
                                layerId,
                                spec.tilesetId,
                                caps[`${fx}-${fy}`]?.context,
                                {
                                    ...fragmentMeta.context,
                                    ...(context || {}),
                                    primary: isAnchorPos,
                                },
                            )
                            if (entityId) {
                                if (!entryObject.context) {
                                    entryObject.context = {}
                                }
                                entryObject.context.entityId = entityId
                            }
                            addFor(x, y, entryObject)
                        }
                    })

                    clearPointsForLayers.forEach(layerId => {
                        const curX = anchorPos ? fx - anchorPos.x : fx
                        const curY = anchorPos ? fy - anchorPos.y : fy
                        remove(curX + x, curY + y, layerId)
                    })
                }
            }

            if (anchor?.context) {
                const context = JSON.parse(anchor.context)
                if (context?.blocking === "remove") {
                    remove(x, y, "blocking")
                }
                if (context?.occlusion === "remove") {
                    remove(x, y, "occlusion")
                }
            }
        }

        const houseAuto = autotile(house)
        const roofAuto = autotile(roof)
        const floorAuto = autotile(floor)

        for (let y = 0; y < house.length; y++) {
            for (let x = 0; x < house[y].length; x++) {
                // print blocking tiles and occlusion tiles
                const tile = house[y][x]
                if (tile) {
                    const { blocking, occlusion } = decorationsFor(tile.itemType)
                    if (blocking) {
                        entries.push({
                            mapId: roomId,
                            tileSpec: blocking,
                            layerId: "blocking",
                            x: x,
                            y: y,
                        })
                    }
                    if (occlusion) {
                        add(entry(x, y, occlusion.tilePos, "occlusion", occlusion.tilesetId))
                    }
                }

                if (houseAuto[y][x]) {
                    debug.push({
                        text: `${houseAuto[y][x]}`,
                        x: x * 32,
                        y: y * 32,
                    })
                }
                if (roofAuto[y][x]) {
                    // debug.push({
                    //     text: `${roofAuto[y][x]}`,
                    //     x: x * 32,
                    //     y: y * 32,
                    // })
                }
                if (floorAuto[y][x]) {
                    debug.push({
                        text: `${floorAuto[y][x]}`,
                        x: x * 32,
                        y: y * 32,
                    })
                }

                //
                // roof
                //
                if (roof[y][x] && ![18, 8, 15, 14, 6, 3, 34, 26, 42, 28, 13, 1, 5, 2].includes(roofAuto[y][x])) {
                    add(entry(x, y, blockingTileSpec.tilePos, "skyTrigger"))
                }

                if (roofAuto[y][x] === 47) {
                    add(entry(x, y, 468, "sky"))
                }

                if (roofAuto[y][x] === 42) {
                    add(entry(x, y, 449, "sky"))
                }
                if (roofAuto[y][x] === 36) {
                    add(entry(x, y, 464, "sky"))
                }
                if (roofAuto[y][x] === 46) {
                    add(entry(x, y, 465, "sky"))
                }
                if (roofAuto[y][x] === 12) {
                    add(entry(x, y, 481, "sky"))
                }
                if (roofAuto[y][x] === 28) {
                    add(entry(x, y, 466, "sky"))
                }
                if (roofAuto[y][x] === 34) {
                    add(entry(x, y, 448, "sky"))
                }
                if (roofAuto[y][x] === 26) {
                    add(entry(x, y, 450, "sky"))
                }
                if (roofAuto[y][x] === 7) {
                    add(entry(x, y, 480, "sky"))
                }
                if (roofAuto[y][x] === 4) {
                    add(entry(x, y, 482, "sky"))
                }

                //
                if (roofAuto[y][x] === 18) {
                    add(entry(x, y, 451, "sky"))
                }
                if (roofAuto[y][x] === 8) {
                    add(entry(x, y, 452, "sky"))
                }
                if (roofAuto[y][x] === 15) {
                    add(entry(x, y, 453, "sky"))
                }
                if (roofAuto[y][x] === 14) {
                    add(entry(x, y, 467, "sky"))
                }
                if (roofAuto[y][x] === 6) {
                    add(entry(x, y, 483, "sky"))
                }
                if (roofAuto[y][x] === 3) {
                    add(entry(x, y, 484, "sky"))
                }

                if (roofAuto[y][x] === 5) {
                    add(entry(x, y, 455, "sky"))
                }
                if (roofAuto[y][x] === 2) {
                    add(entry(x, y, 454, "sky"))
                }

                if (roofAuto[y][x] === 13) {
                    add(entry(x, y, 485, "sky"))
                }
                if (roofAuto[y][x] === 1) {
                    add(entry(x, y, 469, "sky"))
                }

                if (roofAuto[y][x] === 21) {
                    add(entry(x, y, 404, "sky"))
                }
                if (roofAuto[y][x] === 9) {
                    add(entry(x, y, 420, "sky"))
                }
                if (roofAuto[y][x] === 19) {
                    add(entry(x, y, 435, "sky"))
                }
                if (roofAuto[y][x] === 16) {
                    add(entry(x, y, 436, "sky"))
                }

                if (roofAuto[y][x] === 29) {
                    add(entry(x, y, 416, "sky"))
                }
                if (roofAuto[y][x] === 37) {
                    add(entry(x, y, 417, "sky"))
                }

                if (roofAuto[y][x] === 17) {
                    add(entry(x, y, 466, "sky"))
                }
                if (roofAuto[y][x] === 22) {
                    add(entry(x, y, 419, "sky"))
                }

                if (roofAuto[y][x] === 20) {
                    add(entry(x, y, 464, "sky"))
                }
                if (roofAuto[y][x] === 10) {
                    add(entry(x, y, 481, "sky"))
                }
                if (roofAuto[y][x] === 27) {
                    add(entry(x, y, 466, "sky"))
                }
                if (roofAuto[y][x] === 35) {
                    add(entry(x, y, 464, "sky"))
                }
                if (roofAuto[y][x] === 11) {
                    add(entry(x, y, 481, "sky"))
                }

                // default
                if (roofAuto[y][x] && !exists(x, y, "sky")) {
                    add(entry(x, y, 465, "sky"))
                }

                //
                // floor
                //
                if (floor[y][x]?.fragmentPointer) {
                    handleFragmentPointer(x, y, floor[y][x]?.fragmentPointer)
                } else {
                    if (floorAuto[y][x] == 47) {
                        add(entry(x, y, 38, "map2", "floors"))
                    }
                    if (floorAuto[y][x] == 34) {
                        add(entry(x, y, 0, "map2", "floors"))
                    }
                    if (floorAuto[y][x] == 42) {
                        add(entry(x, y, 1, "map2", "floors"))
                    }
                    if (floorAuto[y][x] == 26) {
                        add(entry(x, y, 2, "map2", "floors"))
                    }

                    if (floorAuto[y][x] == 36) {
                        add(entry(x, y, 32, "map2", "floors"))
                    }
                    if (floorAuto[y][x] == 46) {
                        add(entry(x, y, 33, "map2", "floors"))
                    }
                    if (floorAuto[y][x] == 28) {
                        add(entry(x, y, 34, "map2", "floors"))
                    }

                    if (floorAuto[y][x] == 7) {
                        add(entry(x, y, 64, "map2", "floors"))
                    }
                    if (floorAuto[y][x] == 12) {
                        add(entry(x, y, 65, "map2", "floors"))
                    }
                    if (floorAuto[y][x] == 4) {
                        add(entry(x, y, 66, "map2", "floors"))
                    }

                    if (floorAuto[y][x] == 18) {
                        add(entry(x, y, 5, "map2", "floors"))
                    }
                    if (floorAuto[y][x] == 8) {
                        add(entry(x, y, 6, "map2", "floors"))
                    }
                    if (floorAuto[y][x] == 15) {
                        add(entry(x, y, 7, "map2", "floors"))
                    }
                    if (floorAuto[y][x] == 14) {
                        add(entry(x, y, 37, "map2", "floors"))
                    }
                    if (floorAuto[y][x] == 6) {
                        add(entry(x, y, 69, "map2", "floors"))
                    }
                    if (floorAuto[y][x] == 3) {
                        add(entry(x, y, 70, "map2", "floors"))
                    }

                    if (floorAuto[y][x] == 13) {
                        add(entry(x, y, 71, "map2", "floors"))
                    }
                    if (floorAuto[y][x] == 1) {
                        add(entry(x, y, 39, "map2", "floors"))
                    }
                    if (floorAuto[y][x] == 5) {
                        add(entry(x, y, 67, "map2", "floors"))
                    }
                    if (floorAuto[y][x] == 2) {
                        add(entry(x, y, 68, "map2", "floors"))
                    }

                    if (floorAuto[y][x] == 43) {
                        add(entry(x, y, 9, "map2", "floors"))
                    }
                    if (floorAuto[y][x] == 31) {
                        add(entry(x, y, 42, "map2", "floors"))
                    }
                    if (floorAuto[y][x] == 25) {
                        add(entry(x, y, 73, "map2", "floors"))
                    }
                    if (floorAuto[y][x] == 40) {
                        add(entry(x, y, 40, "map2", "floors"))
                    }

                    if (floorAuto[y][x] === 29) {
                        add(entry(x, y, 3, "map2", "floors"))
                    }
                    if (floorAuto[y][x] === 37) {
                        add(entry(x, y, 4, "map2", "floors"))
                    }

                    if (floorAuto[y][x] === 20) {
                        add(entry(x, y, 32, "map2", "floors"))
                    }
                    if (floorAuto[y][x] === 35) {
                        add(entry(x, y, 32, "map2", "floors"))
                    }
                    if (floorAuto[y][x] === 17) {
                        add(entry(x, y, 34, "map2", "floors"))
                    }
                    if (floorAuto[y][x] === 27) {
                        add(entry(x, y, 34, "map2", "floors"))
                    }

                    if (floorAuto[y][x] === 29) {
                        add(entry(x, y, 1, "map2", "floors"))
                    }
                    if (floorAuto[y][x] === 37) {
                        add(entry(x, y, 1, "map2", "floors"))
                    }
                    if (floorAuto[y][x] === 10) {
                        add(entry(x, y, 65, "map2", "floors"))
                    }
                    if (floorAuto[y][x] === 11) {
                        add(entry(x, y, 65, "map2", "floors"))
                    }

                    if (floorAuto[y][x] === 33) {
                        add(entry(x, y, 3, "map2", "floors"))
                    }
                    if (floorAuto[y][x] === 44) {
                        add(entry(x, y, 35, "map2", "floors"))
                    }
                    if (floorAuto[y][x] === 45) {
                        add(entry(x, y, 36, "map2", "floors"))
                    }
                    if (floorAuto[y][x] === 41) {
                        add(entry(x, y, 4, "map2", "floors"))
                    }
                    if (floorAuto[y][x] === 21) {
                        add(entry(x, y, 1, "map2", "floors"))
                    }
                    if (floorAuto[y][x] === 9) {
                        add(entry(x, y, 65, "map2", "floors"))
                    }
                    if (floorAuto[y][x] === 16) {
                        add(entry(x, y, 34, "map2", "floors"))
                    }
                    if (floorAuto[y][x] === 19) {
                        add(entry(x, y, 32, "map2", "floors"))
                    }

                    // default
                    if (floorAuto[y][x] && !exists(x, y, "map2")) {
                        add(entry(x, y, 33, "map2", "floors"))
                    }
                }

                if (floor2[y][x]?.fragmentPointer) {
                    handleFragmentPointer(x, y, floor2[y][x]?.fragmentPointer)
                }
                if (decor[y][x]?.fragmentPointer) {
                    handleFragmentPointer(x, y, decor[y][x]?.fragmentPointer)
                }
            }
        }

        for (let y = 0; y < house.length; y++) {
            for (let x = 0; x < house[y].length; x++) {
                //
                // walls: each material's rules from content/game/maps/autotiles.json, row-major
                //
                HouseBuilder.autotilers.get(house[y][x]?.itemType)?.(x, y, houseAuto, {
                    add,
                    addFor,
                    exists,
                    entry,
                })

                //
                // entities
                //
                if (entity[y][x]?.fragmentPointer) {
                    handleFragmentPointer(
                        x,
                        y,
                        entity[y][x]?.fragmentPointer,
                        entity[y][x]?.entityId,
                        entity[y][x]?.context,
                    )
                }
            }
        }

        this.featureSpec = {
            dimensions: {
                w: width,
                h: height,
            },
            mapEntries: entries,
            fragments: [],
            debug,
        }
    }

    static createConfig = (blockingTileSpec: TileSpec, map: any): HouseBuilderProps => {
        if (!map?.layers) throw new Error("A generated house needs the Tiled house template from content (maps/sources/housetemplate.tmj)")
        const houseData = map.layers.find(l => l.properties.find(p => p.name === "blocking" && p.value === true)).data
        const roofData = map.layers.find(l => l.properties.find(p => p.name === "sky" && p.value === true)).data

        const height = map.height
        const width = map.width

        const house: TileMapBase<GridCell> = blankMap(width, height)
        const roof: TileMapBase<GridCell> = blankMap(width, height)
        const entity: TileMapBase<GridCell> = blankMap(width, height)
        const floor: TileMapBase<GridCell> = blankMap(width, height)
        const floor2: TileMapBase<GridCell> = blankMap(width, height)
        const decor: TileMapBase<GridCell> = blankMap(width, height)

        houseData.forEach((next, index) => {
            const x = index % height
            const y = Math.floor(index / width)
            if (next) {
                house[y][x] = {
                    itemType: "wall2-wood",
                }
            }
        })
        roofData.forEach((next, index) => {
            const x = index % width
            const y = Math.floor(index / height)
            if (next) {
                roof[y][x] = {
                    itemType: "roof-wood",
                }
            }
        })

        return {
            blockingTileSpec,
            house,
            roof,
            entity,
            floor,
            floor2,
            decor,
        }
    }
}

// Which item builds which wall material, and how: rebuilt from the content on every activation, so
// a wall material deployed live from Sprite Lab is buildable without a restart.
bindContent(catalog => {
    const walls = autotileTables(catalog.source.autotiles as any).walls
    const materials = wallMaterialsByItem(walls) as Map<ItemTypes, string>
    const definitions = new Map<ItemTypes, WallAutotileDefinition>()
    const autotilers = new Map<ItemTypes, Autotiler>()
    materials.forEach((id, itemType) => {
        const definition = walls.find(d => d.id === id)
        definitions.set(itemType, definition)
        autotilers.set(itemType, wallAutotiler(definition))
    })
    return { materials, definitions, autotilers }
}, next => {
    HouseBuilder.wallMaterials = next.materials
    HouseBuilder.wallDefinitions = next.definitions
    HouseBuilder.autotilers = next.autotilers
})
